import { JSX } from 'react';
import { WidgetProps } from '@rjsf/utils';
import {
    Select,
    MenuItem,
    SelectChangeEvent
} from '@mui/material'
import { nokAiModels } from '../../../../../defaults/defaults';

/** The shape `nokAiModels` carries. The alias in `defaults.ts` is not exported. */
type ModelChoice = { id: string, name: string };

/**
 * The models a workflow node may run on.
 *
 * Driven by `nokAiModels` - the platform's own list, which the Daive chat panel and the Nok edit
 * screens already share - rather than by a copy. The copy that used to live in this file had
 * drifted badly: six Ollama models the platform no longer runs, **a `DeepSeekAPI` entry nothing can
 * build** (`llm_factory.prepare_llm` answers None for it, so a node set to it fails inside the
 * agent rather than at the selector), and not one of the Mistral or Claude models the Operator has
 * supported for months.
 *
 * Ollama's hosted models are left out by the user's decision (2026-09-16).
 *
 * **A value already stored is always offered, even when it is no longer on the list.** MUI renders
 * a Select whose value is absent from its children as blank, which reads as "no model chosen" on a
 * node that is configured and running - and an admin who then saves the form silently clears it.
 * That is the trap the Compliance Sniffer's status list fell into, and it is worth the extra entry
 * to stay out of it.
 *
 * @param current the selector the node currently holds, if any
 */
export const workflowLlmChoices: (current?: string) => ModelChoice[] =
    (current?: string): ModelChoice[] => {
        const offered: ModelChoice[] = (nokAiModels as ModelChoice[])
            .filter((model: ModelChoice): boolean => !model.id.includes('-cloud'));

        const alreadyOffered: boolean = offered
            .some((model: ModelChoice): boolean => model.id === current);

        if (typeof current === 'string' && current.length > 0 && !alreadyOffered) {
            return [{ id: current, name: current + ' (no longer offered)' }, ...offered];
        }

        return offered;
    };

export const LLMModelWidget: (props: WidgetProps) => JSX.Element = (props: WidgetProps): JSX.Element => {
    const { value, onChange } = props;

    function handleChange(event: SelectChangeEvent): void {
        onChange(event.target.value);
    }

    return (
        <>
            <span>{ "LLM" }</span>
            <Select value={ value ?? '' } onChange={ handleChange } name="LLLM">
                { workflowLlmChoices(value).map( (model: ModelChoice): JSX.Element => (
                    <MenuItem key={ 'workflow_llm_' + model.id } value={ model.id }>
                        { model.name }
                    </MenuItem>
                ) ) }
            </Select>
        </>
    );
}
